/**
 * ServerNox Native Bridge (entry-first)
 * Runs before app modules so /api requests are rewritten on Capacitor.
 */

import {
  getDeviceNotificationRouting,
  notificationCategoryForEvent,
  notificationPresentation,
  notificationTargetUrl,
} from './lib/operationNotifications'
import { notificationActions } from './lib/notificationActions'
import { navigateApp } from './lib/appNavigation'
import { syncNativeShortcuts, getNativeInitialRoute } from './lib/nativeShortcuts'

type LooseObject = Record<string, any>;
type NativePlugin = Record<string, any>;

interface CapacitorRuntime {
  isNativePlatform?: () => boolean;
  getPlatform?: () => string;
  Plugins?: Record<string, NativePlugin | undefined>;
}

interface ServerNoxWindow extends Window {
  Capacitor?: CapacitorRuntime;
  SCPNative?: Record<string, unknown>;
  _scpFcmListenersBound?: boolean;
  _scpChannelsCreated?: boolean;
}

(function () {
  'use strict';

  var API_ORIGIN = 'https://app.servernox.com';
  var nativeWindow = window as ServerNoxWindow;
  var isCapacitor = !!nativeWindow.Capacitor?.isNativePlatform?.();
  if (isCapacitor) document.documentElement.setAttribute('data-servernox-native', nativeWindow.Capacitor?.getPlatform?.() || 'native');

  function currentTenantScope() {
    try {
      var token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
      var segment = (token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/');
      segment += '='.repeat((4 - segment.length % 4) % 4);
      var payload = JSON.parse(atob(segment));
      return String(payload.membership_id || payload.org_id || payload.sub || 'anonymous');
    } catch (_) { return 'anonymous'; }
  }

  if (isCapacitor) {
    var origOpen = XMLHttpRequest.prototype.open;
    XMLHttpRequest.prototype.open = function (this: XMLHttpRequest, method: string, url: string | URL, async: boolean = true, user?: string | null, password?: string | null) {
      if (typeof url === 'string') {
        if (url === '/api' || url.startsWith('/api/')) {
          url = API_ORIGIN + url;
        }
      }
      return origOpen.call(this, method, url, Boolean(async), user, password);
    } as typeof XMLHttpRequest.prototype.open;

    var origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function (this: XMLHttpRequest, body?: Document | XMLHttpRequestBodyInit | null) {
      try { this.setRequestHeader('X-ServerNox-Client', 'android-capacitor'); } catch (_) {}
      return origSend.call(this, body);
    };
  }

  var nativeFetch = window.fetch.bind(window);
  function addClientHeader(init?: RequestInit): RequestInit | undefined {
    if (!isCapacitor) return init;
    var out = Object.assign({}, init || {});
    var h = new Headers(out.headers || {});
    if (!h.has('X-ServerNox-Client')) h.set('X-ServerNox-Client', 'android-capacitor');
    out.headers = h;
    return out;
  }

  window.fetch = function (input, init) {
    try {
      if (isCapacitor) {
        if (typeof input === 'string') {
          if (input === '/api' || input.startsWith('/api/')) {
            return nativeFetch(API_ORIGIN + input, addClientHeader(init));
          }
        } else if (input instanceof Request && typeof input.url === 'string') {
          if (input.url === '/api' || input.url.startsWith('/api/')) {
            var reqHeaders = new Headers(input.headers || {});
            reqHeaders.set('X-ServerNox-Client', 'android-capacitor');
            return nativeFetch(
              new Request(API_ORIGIN + input.url, Object.assign({}, input, { headers: reqHeaders })),
              addClientHeader(init)
            );
          }
        }
      }
    } catch (_) {}
    return nativeFetch(input, init);
  };

  if (isCapacitor) {
    var OrigWS = window.WebSocket;
    function PatchedWebSocket(url: string | URL, protocols?: string | string[]) {
      if (typeof url === 'string') {
        try {
          var u = new URL(url);
          if (u.pathname.startsWith('/api/')) {
            url = 'wss://app.servernox.com' + u.pathname + (u.search || '');
          }
        } catch (_) {}
      }
      if (protocols !== undefined) {
        return new OrigWS(url, protocols);
      }
      return new OrigWS(url);
    }
    PatchedWebSocket.prototype = OrigWS.prototype;
    PatchedWebSocket.CONNECTING = OrigWS.CONNECTING;
    PatchedWebSocket.OPEN = OrigWS.OPEN;
    PatchedWebSocket.CLOSING = OrigWS.CLOSING;
    PatchedWebSocket.CLOSED = OrigWS.CLOSED;
    window.WebSocket = PatchedWebSocket as unknown as typeof WebSocket;

    // Aynı-origin hedefleri yeni WebView/Chrome örneğine kaçırma. Harici web
    // adreslerini ise Capacitor Browser (Android Custom Tab) içinde aç: hem
    // sistem geri tuşu hem de görünür kapatma düğmesi kullanıcıyı ServerNox'a
    // güvenilir biçimde geri getirir. `window.open` bazı WebView sürümlerinde
    // geçmiş girdisi oluşturmadan uygulamanın üstüne yerleşebiliyordu.
    document.addEventListener('click', function (event) {
      var eventElement = event.target instanceof Element ? event.target : null;
      var node = eventElement?.closest<HTMLAnchorElement>('a[href]') || null;
      if (!node) return;
      try {
        // Chat owns APK/workspace-document links so it can use the native
        // downloader or Markdown editor. Capture-phase navigation here used
        // to win before React and turn /apk/... into http://localhost/apk/....
        if (node.getAttribute('data-servernox-chat-link') === 'handled') return;
        var target = new URL(node.href, window.location.href);
        var isApkTarget = (
          (target.origin === API_ORIGIN && target.pathname.startsWith('/apk/')) ||
          (target.origin === 'https://servernox.com' && target.pathname.startsWith('/download/')) ||
          ((target.origin === window.location.origin || target.origin === API_ORIGIN) && (target.pathname.startsWith('/apk/') || target.pathname.startsWith('/download/')))
        ) && target.pathname.toLowerCase().endsWith('.apk');
        if (isApkTarget) {
          if (!isCapacitor) {
            return;
          }
          event.preventDefault();
          var filename = decodeURIComponent(target.pathname.split('/').pop() || 'servernox.apk').replace(/[\\/:*?"<>|\x00-\x1f]/g, '_');
          downloadPublicFile(target.href, filename, 'application/vnd.android.package-archive', true).catch(function (error) {
            if (/iptal/i.test(String(error && error.message || error))) return;
            var Browser = nativeWindow.Capacitor?.Plugins?.Browser;
            if (Browser && Browser.open) {
              Browser.open({ url: 'https://servernox.com/download/', presentationStyle: 'popover', toolbarColor: '#12151f' }).catch(function () {
                window.open('https://servernox.com/download/', '_blank');
              });
            } else {
              window.open('https://servernox.com/download/', '_blank');
            }
          });
          return;
        }
        if (target.origin === window.location.origin || target.origin === API_ORIGIN) {
          var targetPath = target.pathname + target.search + target.hash;
          if (navigateApp(targetPath)) {
            event.preventDefault();
            return;
          }
          event.preventDefault();
          window.location.assign(targetPath);
          return;
        }
        if ((target.protocol === 'https:' || target.protocol === 'http:') && !node.hasAttribute('download')) {
          var Browser = nativeWindow.Capacitor?.Plugins?.Browser;
          if (Browser && Browser.open) {
            event.preventDefault();
            Browser.open({ url: target.href, presentationStyle: 'popover', toolbarColor: '#12151f' });
          }
        }
      } catch (_) {}
    }, true);
  }

  function notificationPayload(notification?: LooseObject, data?: LooseObject) {
    var n: LooseObject = notification || {};
    var d: LooseObject = Object.assign({}, n.extra || {}, n.data || {}, data || {});
    const actionSet = notificationActions(d);
    return {
      title: d.scp_title || d.title || n.title || '',
      body: d.scp_body || d.body || n.body || '',
      id: d.scp_id || d.id || '',
      created_at: d.created_at || '',
      event: d.event || '',
      url: notificationTargetUrl(d.url, '/notifications/history', d.event),
      ...(actionSet ? { action_token: actionSet.token, action_kind: actionSet.kind, actions: actionSet.actions } : {}),
    };
  }

  function openNotificationPayload(payload?: LooseObject) {
    var target = notificationTargetUrl(payload && payload.url, '/notifications/history', payload && payload.event);
    payload = Object.assign({}, payload || {}, { url: target });
    // Navigation intents may be persisted; one-use decision capabilities may not.
    delete payload.action_token;
    delete payload.action_kind;
    delete payload.actions;
    try { sessionStorage.setItem('scp_pending_notification:' + currentTenantScope(), JSON.stringify(payload)); } catch (_) {}
    window.dispatchEvent(new CustomEvent('servernox-notification-tap', { detail: payload }));
    var current = window.location.pathname + window.location.search;
    if (current !== target) {
      if (navigateApp(target)) return;
      try {
        var parsed = new URL(target, window.location.origin);
        if (window.location.pathname === '/ai-coding' && parsed.pathname === '/ai-coding') {
          window.history.replaceState(null, '', parsed.pathname + parsed.search + parsed.hash);
          return;
        }
      } catch (_) {}
      window.location.assign(target);
    }
  }

  function dispatchInAppNotification(payload: LooseObject) {
    window.dispatchEvent(new CustomEvent('servernox-in-app-notification', { detail: payload }));
  }

  async function presentForegroundNotification(payload: LooseObject) {
    const category = notificationCategoryForEvent(payload.event);
    const presentation = notificationPresentation(getDeviceNotificationRouting(), category, true);
    const normalized = Object.assign({}, payload, { category: category });
    if (presentation.inApp) dispatchInAppNotification(normalized);
    if (presentation.system) {
      await notify(normalized.title || 'ServerNox', normalized.body || '', undefined, normalized.url, normalized);
    }
  }

  // PWA service worker açık bir pencere bulduğunda hedefi ayrıca message ile
  // yollar. navigate() desteği zayıf/eski WebView'larda da bildirime dokunma
  // doğru sohbeti açsın; aynı güvenli iç-URL doğrulaması burada da geçerli.
  if (navigator.serviceWorker && navigator.serviceWorker.addEventListener) {
    navigator.serviceWorker.addEventListener('message', function (event) {
      if (event.data && event.data.type === 'SERVERNOX_NOTIFICATION_TAP') {
        openNotificationPayload(notificationPayload({}, event.data.payload || {}));
      } else if (event.data && event.data.type === 'SERVERNOX_FOREGROUND_NOTIFICATION') {
        // Service worker yönlendirme kararını verdi; burada ikinci kez işletim
        // sistemi bildirimi üretmeden yalnız uygulama içi merkezi besle.
        dispatchInAppNotification(notificationPayload({}, event.data.payload || {}));
      }
    });
  }

  var _notificationPermsGranted = false;
  async function notify(title: string, body: string, id?: number, url?: string, extra?: LooseObject) {
    if (!isCapacitor) {
      if (window.Notification && Notification.permission === 'granted') {
        new Notification(title, { body: body, icon: '/logo-192.png?v=brand-20260909' });
      }
      return;
    }
    var LocalNotifications = nativeWindow.Capacitor?.Plugins?.LocalNotifications;
    if (!LocalNotifications) return;
    if (!_notificationPermsGranted) {
      var perms = await LocalNotifications.requestPermissions();
      if (!perms || perms.display !== 'granted') return;
      _notificationPermsGranted = true;
    }
    if (LocalNotifications.createChannel && !nativeWindow._scpChannelsCreated) {
      nativeWindow._scpChannelsCreated = true;
      try {
        await LocalNotifications.createChannel({
          id: 'servernox-critical',
          name: 'Kritik Sistem Alarmları',
          description: 'Servis durması, yüksek disk/RAM ve güvenlik uyarıları',
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true,
        });
        await LocalNotifications.createChannel({
          id: 'servernox-tasks',
          name: 'İş ve Görev Bildirimleri',
          description: 'Tarama, yedekleme ve sistem bakım bildirimleri',
          importance: 3,
          visibility: 1,
          vibration: false,
        });
        await LocalNotifications.createChannel({
          id: 'servernox-media',
          name: 'Medya ve Arka Plan Durumu',
          description: 'Devam eden tarama ve medya çalma durumu',
          importance: 2,
          visibility: 0,
          vibration: false,
        });
      } catch (_) {}
    }
    var targetChannel = (extra && extra.channelId) || (id === 9981 ? 'servernox-media' : (String(title).toLowerCase().includes('kritik') || String(title).toLowerCase().includes('alarm') ? 'servernox-critical' : 'servernox-tasks'));
    await LocalNotifications.schedule({
      notifications: [{
        id: id || (Date.now() % 2147483000),
        title: String(title || 'ServerNox'),
        body: String(body || ''),
        channelId: targetChannel,
        smallIcon: 'ic_stat_servernox',
        extra: Object.assign({}, extra || {}, {
          scp_title: String(title || 'ServerNox'),
          scp_body: String(body || ''),
          url: notificationTargetUrl(url),
        }),
      }],
    });
  }

  async function cancelNotification(id: number) {
    if (!isCapacitor) return;
    var LocalNotifications = nativeWindow.Capacitor?.Plugins?.LocalNotifications;
    if (!LocalNotifications) return;
    try {
      await LocalNotifications.cancel({ notifications: [{ id: id }] });
    } catch (_) {}
  }

  async function requestNotificationPermission() {
    if (!isCapacitor) {
      if (!window.Notification) return 'unsupported';
      if (Notification.permission === 'granted') return 'granted';
      if (Notification.permission === 'denied') return 'denied';
      return Notification.requestPermission();
    }
    var LocalNotifications = nativeWindow.Capacitor?.Plugins?.LocalNotifications;
    if (!LocalNotifications) return 'unsupported';
    var perms = await LocalNotifications.requestPermissions();
    return (perms && perms.display) || 'denied';
  }

  async function notificationPermissionStatus() {
    if (!isCapacitor) {
      return window.Notification ? Notification.permission : 'unsupported';
    }
    var LocalNotifications = nativeWindow.Capacitor?.Plugins?.LocalNotifications;
    if (!LocalNotifications) return 'unsupported';
    var perms = await LocalNotifications.checkPermissions();
    return (perms && perms.display) || 'denied';
  }

  async function registerFcmPush() {
    if (!isCapacitor) return { ok: false, reason: 'not_native' };
    var PushNotifications = nativeWindow.Capacitor?.Plugins?.PushNotifications;
    if (!PushNotifications) return { ok: false, reason: 'plugin_unavailable' };

    if (PushNotifications.createChannel) {
      try {
        await PushNotifications.createChannel({
          id: 'servernox-ai',
          name: 'AI sohbetleri',
          description: 'Codex, Claude ve ServerNox sistem mesajları',
          importance: 5,
          visibility: 1,
          vibration: true,
          lights: true,
        });
      } catch (_) {}
    }

    // Aynı SPA oturumunda hesap değişmiş olabilir. Listener'ları ikinci
    // kez ekleme ama register() çağrısını yinele ki cihaz token'ı yeni
    // üyeliğe devredilsin.
    if (nativeWindow._scpFcmListenersBound) {
      await PushNotifications.register();
      return { ok: true, reason: 're_registered' };
    }
    nativeWindow._scpFcmListenersBound = true;
    var LocalNotifications = nativeWindow.Capacitor?.Plugins?.LocalNotifications;
    if (LocalNotifications && LocalNotifications.addListener) {
      await LocalNotifications.addListener('localNotificationActionPerformed', function (action: LooseObject) {
        var n = action.notification || {};
        openNotificationPayload(notificationPayload(n, n.extra || {}));
      });
    }

    await PushNotifications.addListener('registration', async function (tokenData: { value?: string }) {
      var fcmToken = tokenData.value;
      if (!fcmToken) return;
      var authToken = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
      try { localStorage.setItem('scpFcmToken:' + currentTenantScope(), fcmToken); } catch (_) {}
      try {
        var headers: Record<string, string> = {
          'Content-Type': 'application/json',
          'X-ServerNox-Client': 'android-capacitor',
        };
        if (authToken) headers.Authorization = 'Bearer ' + authToken;
        await fetch(API_ORIGIN + '/api/push/fcm/subscribe', {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            fcm_token: fcmToken,
            device_info: {
              platform: nativeWindow.Capacitor?.getPlatform?.() || 'android',
              user_agent: navigator.userAgent || '',
              // Older APKs do not own the FCM service required to render
              // notification action buttons. The backend uses this explicit
              // capability for a lossless fallback to a normal tap-to-open
              // notification until the device has upgraded.
              ai_notification_actions: true,
              // New APKs can enforce foreground/background presentation on
              // device. Older APK endpoints keep receiving the ordinary FCM
              // notification payload until they re-register with this flag.
              contextual_notification_routing: true,
            },
          }),
        });
      } catch (_) {}
    });

    await PushNotifications.addListener('registrationError', function (err: unknown) {
      console.warn('[SCPNative] FCM kayıt hatası', err);
    });

    await PushNotifications.addListener('pushNotificationReceived', function (notification: LooseObject) {
      var payload = notificationPayload(notification, notification.data || {});
      presentForegroundNotification(payload).catch(function () {});
    });

    // Bildirime tıklanınca içeriği doğrudan aç
    await PushNotifications.addListener('pushNotificationActionPerformed', function (action: LooseObject) {
      var n = action.notification || {};
      var d = n.data || {};
      openNotificationPayload(notificationPayload(n, d));
    });

    var perms = await PushNotifications.requestPermissions();
    if (!perms || perms.receive !== 'granted') return { ok: false, reason: (perms && perms.receive) || 'denied' };
    await PushNotifications.register();
    return { ok: true };
  }

  async function unregisterCurrentFcmEndpoint() {
    var authToken = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    var key = 'scpFcmToken:' + currentTenantScope();
    var fcmToken = '';
    try { fcmToken = localStorage.getItem(key) || ''; } catch (_) {}
    if (!authToken || !fcmToken) return { ok: true, reason: 'not_registered' };
    try {
      var response = await fetch(API_ORIGIN + '/api/push/fcm/subscribe', {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'X-ServerNox-Client': 'android-capacitor',
          Authorization: 'Bearer ' + authToken,
        },
        body: JSON.stringify({ fcm_token: fcmToken }),
      });
      if (response.ok || response.status === 404) {
        try { localStorage.removeItem(key); } catch (_) {}
      }
      return { ok: response.ok || response.status === 404 };
    } catch (_) { return { ok: false }; }
  }

  async function setPreference(key: string, value: string) {
    if (!isCapacitor) { localStorage.setItem('scp_pref_' + key, value); return; }
    var Preferences = nativeWindow.Capacitor?.Plugins?.Preferences;
    if (Preferences) await Preferences.set({ key: key, value: String(value) });
  }

  async function getPreference(key: string): Promise<string | null> {
    if (!isCapacitor) return localStorage.getItem('scp_pref_' + key);
    var Preferences = nativeWindow.Capacitor?.Plugins?.Preferences;
    if (!Preferences) return null;
    var result = await Preferences.get({ key: key });
    return result && result.value !== null ? result.value : null;
  }

  function nativeFileTransfer() {
    return nativeWindow.Capacitor?.Plugins?.NativeFileTransfer;
  }

  // Dosya aktarımı WebView fetch/XHR katmanından değil, doğrudan Android
  // eklentisinden çıktığı için resolveApiPath() interceptor'ına uğramaz.
  // Aktif bir ek sunucu varsa native aktarım URL'sini de hub proxy'sine
  // yönlendirmeliyiz; aksi halde ekranda uzak sunucu seçili görünürken dosya
  // ana sunucuya yükleniyor/ana sunucudan indiriliyordu.
  function nativeFilesUrl(action: 'upload' | 'download', query?: string) {
    var token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    var scope = '';
    try {
      var segment = (token.split('.')[1] || '').replace(/-/g, '+').replace(/_/g, '/');
      segment += '='.repeat((4 - segment.length % 4) % 4);
      var payload = JSON.parse(atob(segment));
      scope = String(payload.membership_id || payload.org_id || payload.sub || '');
    } catch (_) {}
    var activeServer = scope ? localStorage.getItem('scpActiveServer:' + scope) : '';
    var basePath = activeServer
      ? '/api/proxy/' + encodeURIComponent(activeServer) + '/files/' + action
      : '/api/proxy/__servernox_no_server__/files/' + action;
    return API_ORIGIN + basePath + (query || '');
  }

  // Doğrudan aktarım bileti yalnız Agent'ın aktarım yoluna gider; panel oturum
  // anahtarı hiçbir koşulda bu adreslere gönderilmez.
  function directTransferRequest(direct: LooseObject | undefined, operation: string) {
    if (!direct || typeof direct.url !== 'string' || typeof direct.authorization !== 'string') return null;
    if (direct.authorization.indexOf('ServerNox-Transfer ') !== 0) return null;
    try {
      var target = new URL(direct.url);
      if (target.protocol !== 'https:' || target.pathname !== '/transfer/v1/' + operation || target.username || target.password) return null;
      return { url: target.href, authorization: direct.authorization };
    } catch (_) {
      return null;
    }
  }

  async function uploadFromDevice(path: string, onProgress?: (event: LooseObject) => void, direct?: LooseObject) {
    if (!isCapacitor) return { handled: false };
    var transfer = nativeFileTransfer();
    if (!transfer) throw new Error('Yerel dosya aktarımı hazır değil; uygulamayı güncelleyin.');
    var token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    if (!token) throw new Error('Oturum bulunamadı');
    var directUpload = directTransferRequest(direct, 'upload');
    let listener: { remove?: () => Promise<void> } | null = null;
    try {
      if (typeof onProgress === 'function' && transfer.addListener) {
        listener = await transfer.addListener('uploadProgress', function (event: LooseObject) {
          try { onProgress(event || {}); } catch (_) {}
        });
      }
      var proxyUpload = { url: nativeFilesUrl('upload'), authorization: 'Bearer ' + token };
      var result = await transfer.pickAndUpload(Object.assign({
        url: directUpload ? directUpload.url : proxyUpload.url,
        path: path,
        authorization: directUpload ? directUpload.authorization : proxyUpload.authorization,
      }, directUpload ? { fallbackUrl: proxyUpload.url, fallbackAuthorization: proxyUpload.authorization } : {}));
      return Object.assign({ handled: true }, result || {});
    } finally {
      if (listener && listener.remove) await listener.remove();
    }
  }

  async function downloadToDevice(path: string, expectedSize?: number, onProgress?: (event: LooseObject) => void) {
    if (!isCapacitor) return { handled: false };
    var transfer = nativeFileTransfer();
    if (!transfer) throw new Error('Yerel dosya aktarımı hazır değil; uygulamayı güncelleyin.');
    var token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    if (!token) throw new Error('Oturum bulunamadı');
    var filename = String(path || '').split('/').pop() || 'download';
    var requestId = 'download-' + Date.now() + '-' + Math.random().toString(16).slice(2);
    let listener: { remove?: () => Promise<void> } | null = null;
    if (typeof onProgress === 'function' && transfer.addListener) {
      listener = await transfer.addListener('downloadProgress', function (event: LooseObject) {
        if (String(event?.requestId || event?.jobId || '') !== requestId) return;
        try { onProgress(event || {}); } catch (_) {}
        if (['done', 'error', 'cancelled'].includes(String(event?.status || ''))) {
          try { void listener?.remove?.(); } catch (_) {}
          listener = null;
        }
      });
    }
    try {
      var result = await transfer.download({
        url: nativeFilesUrl('download', '?path=' + encodeURIComponent(path)),
        authorization: 'Bearer ' + token,
        filename: filename,
        mimeType: 'application/octet-stream',
        expectedSize: Math.max(0, Number(expectedSize || 0)),
        requestId: requestId,
      });
      return Object.assign({ handled: true }, result || {});
    } catch (error) {
      if (listener && listener.remove) await listener.remove();
      throw error;
    }
  }

  async function downloadUrlToDevice(options: LooseObject) {
    if (!isCapacitor) return { handled: false };
    var transfer = nativeFileTransfer();
    if (!transfer?.download) throw new Error('Yerel arka plan indirmesi hazır değil; uygulamayı güncelleyin.');
    var token = sessionStorage.getItem('token') || localStorage.getItem('token') || '';
    if (!token) throw new Error('Oturum bulunamadı');
    var target = new URL(String(options?.url || ''), API_ORIGIN);
    if (target.protocol !== 'https:' || target.origin !== API_ORIGIN || !target.pathname.startsWith('/api/')) {
      throw new Error('Yalnız doğrulanmış ServerNox API indirmeleri kullanılabilir.');
    }
    // Doğrudan adres varsa önce o denenir; yerel servis başarısızlıkta panel adresine geçer.
    var directDownload = directTransferRequest(options?.direct, 'download');
    var proxyDownload = { url: target.href, authorization: 'Bearer ' + token };
    var requestId = String(options?.requestId || 'download-' + Date.now() + '-' + Math.random().toString(16).slice(2));
    var result = await transfer.download(Object.assign({
      url: directDownload ? directDownload.url : proxyDownload.url,
      authorization: directDownload ? directDownload.authorization : proxyDownload.authorization,
      filename: String(options?.filename || target.pathname.split('/').pop() || 'download'),
      mimeType: String(options?.mimeType || 'application/octet-stream'),
      expectedSize: Math.max(0, Number(options?.expectedSize || 0)),
      requestId: requestId,
      openAfterDownload: options?.openAfterDownload === true,
    }, directDownload ? { fallbackUrl: proxyDownload.url, fallbackAuthorization: proxyDownload.authorization } : {}));
    return Object.assign({ handled: true, jobId: requestId, requestId: requestId }, result || {});
  }

  async function subscribeDownloads(callback: (event: LooseObject) => void) {
    if (!isCapacitor) return { handled: false, remove: async function () {} };
    var transfer = nativeFileTransfer();
    if (!transfer?.addListener) throw new Error('Yerel indirme olayları hazır değil; uygulamayı güncelleyin.');
    var listener = await transfer.addListener('downloadProgress', function (event: LooseObject) {
      try { callback(event || {}); } catch (_) {}
    });
    return { handled: true, remove: async function () { await listener?.remove?.(); } };
  }

  async function listNativeDownloads() {
    if (!isCapacitor) return { handled: false, downloads: [] };
    var transfer = nativeFileTransfer();
    if (!transfer?.listDownloads) return { handled: false, downloads: [] };
    return Object.assign({ handled: true, downloads: [] }, await transfer.listDownloads());
  }

  async function cancelNativeDownload(jobId: string) {
    var transfer = nativeFileTransfer();
    if (!isCapacitor || !transfer?.cancelDownload) return { handled: false };
    return Object.assign({ handled: true }, await transfer.cancelDownload({ jobId: String(jobId || '') }));
  }

  async function retryNativeDownload(jobId: string) {
    var transfer = nativeFileTransfer();
    if (!isCapacitor || !transfer?.retryDownload) return { handled: false };
    return Object.assign({ handled: true }, await transfer.retryDownload({ jobId: String(jobId || '') }));
  }

  async function openNativeDownload(jobId: string) {
    var transfer = nativeFileTransfer();
    if (!isCapacitor || !transfer?.openDownload) return { handled: false };
    return Object.assign({ handled: true }, await transfer.openDownload({ jobId: String(jobId || '') }));
  }

  async function dismissNativeDownload(jobId: string) {
    var transfer = nativeFileTransfer();
    if (!isCapacitor || !transfer?.dismissDownload) return { handled: false };
    return Object.assign({ handled: true }, await transfer.dismissDownload({ jobId: String(jobId || '') }));
  }

  async function downloadPublicFile(url: string, filename?: string, mimeType?: string, openAfterDownload?: boolean) {
    if (!isCapacitor) return { handled: false };
    var transfer = nativeFileTransfer();
    if (!transfer || !transfer.downloadPublic) throw new Error('Yerel genel dosya indirme hazır değil; uygulamayı güncelleyin.');
    var target = new URL(String(url || ''), API_ORIGIN);
    var isAllowedOrigin = target.origin === API_ORIGIN || target.origin === 'https://servernox.com';
    if (target.protocol !== 'https:' || !isAllowedOrigin) throw new Error('Yalnız doğrulanmış ServerNox yayınları indirilebilir.');
    var safeFilename = String(filename || target.pathname.split('/').pop() || 'download');

    // Backward compatibility for existing installed APKs:
    // Older native code only accepts host 'app.servernox.com' and path starting with '/apk/'.
    // Mapping any APK download under servernox.com/download/ to app.servernox.com/apk/ ensures
    // both older and newer native clients succeed immediately without 301 redirects.
    var downloadUrl = target.href;
    if (
      (target.origin === 'https://servernox.com' || target.origin === API_ORIGIN) &&
      (target.pathname.startsWith('/download/') || target.pathname.startsWith('/apk/')) &&
      target.pathname.toLowerCase().endsWith('.apk')
    ) {
      downloadUrl = API_ORIGIN + '/apk/' + safeFilename;
    }

    var result = await transfer.downloadPublic({
      url: downloadUrl,
      filename: safeFilename,
      mimeType: String(mimeType || 'application/octet-stream'),
      openAfterDownload: openAfterDownload === true,
    });
    return Object.assign({ handled: true }, result || {});
  }

  async function saveTextToDevice(filename: string, content: string) {
    if (!isCapacitor) return { handled: false };
    var transfer = nativeFileTransfer();
    if (!transfer || !transfer.saveText) throw new Error('Yerel metin kaydı hazır değil; uygulamayı güncelleyin.');
    return Object.assign({ handled: true }, await transfer.saveText({ filename: filename, content: content, mimeType: 'application/octet-stream' }));
  }

  async function openExternalUrl(url: string) {
    if (!isCapacitor) return { handled: false };
    var target = new URL(String(url || ''));
    if (target.protocol !== 'https:' && target.protocol !== 'http:') {
      throw new Error('Yalnız HTTP/HTTPS bağlantıları açılabilir.');
    }
    if (target.username || target.password) {
      throw new Error('Kimlik bilgisi içeren bağlantılar açılamaz.');
    }
    var Browser = nativeWindow.Capacitor?.Plugins?.Browser;
    if (!Browser || !Browser.open) return { handled: false };
    await Browser.open({ url: target.href, presentationStyle: 'popover', toolbarColor: '#12151f' });
    return { handled: true };
  }

  function nativeSpeechRecognition() {
    return isCapacitor ? nativeWindow.Capacitor?.Plugins?.NativeSpeechRecognition : undefined;
  }

  async function speechRecognitionAvailable() {
    var plugin = nativeSpeechRecognition();
    if (!plugin || !plugin.isAvailable) return { available: false, native: isCapacitor };
    try {
      var result = await plugin.isAvailable();
      return { available: result?.available === true, native: true };
    } catch (_) {
      return { available: false, native: true };
    }
  }

  async function startSpeechRecognition(options?: LooseObject) {
    var plugin = nativeSpeechRecognition();
    if (!plugin || !plugin.start || !plugin.addListener) return { handled: false };
    var activePlugin = plugin;
    var closed = false;
    var listeners: LooseObject[] = [];
    var cleanup = async function () {
      if (closed) return;
      closed = true;
      var pending = listeners.splice(0).map(function (listener) {
        try { return listener?.remove?.(); } catch (_) { return undefined; }
      });
      await Promise.allSettled(pending);
    };
    listeners.push(await activePlugin.addListener('speechResult', function (event: LooseObject) {
      if (!closed && typeof options?.onResult === 'function') options.onResult(event || {});
    }));
    listeners.push(await activePlugin.addListener('speechError', function (event: LooseObject) {
      if (!closed && typeof options?.onError === 'function') options.onError(event || {});
    }));
    listeners.push(await activePlugin.addListener('speechState', function (event: LooseObject) {
      if (closed) return;
      if (typeof options?.onState === 'function') options.onState(event || {});
      if (event?.state === 'ended' || event?.state === 'cancelled') {
        if (typeof options?.onEnd === 'function') options.onEnd(event || {});
        void cleanup();
      }
    }));
    try {
      await activePlugin.start({ language: String(options?.language || navigator.language || 'tr-TR') });
    } catch (error) {
      await cleanup();
      throw error;
    }
    var cancel = async function () {
      try { await activePlugin.cancel?.(); } finally { await cleanup(); }
    };
    return { handled: true, native: true, stop: cancel, abort: cancel };
  }

  async function setTerminalKeyboardRaw(raw: boolean) {
    if (!isCapacitor) return { handled: false };
    var plugin = nativeWindow.Capacitor?.Plugins?.NativeKeyboardMode;
    if (!plugin || !plugin.setRawTerminalInput) return { handled: false };
    await plugin.setRawTerminalInput({ raw: raw === true });
    return { handled: true, raw: raw === true };
  }

  function nativeScreenRecorder() {
    return nativeWindow.Capacitor?.Plugins?.NativeScreenRecorder;
  }

  async function screenRecordingAvailable() {
    if (!isCapacitor) return { available: false };
    var plugin = nativeScreenRecorder();
    if (!plugin || typeof plugin.isAvailable !== 'function') return { available: false };
    try {
      var res = await plugin.isAvailable();
      return { available: res?.available === true };
    } catch (_) {
      return { available: false };
    }
  }

  async function startScreenRecording() {
    if (!isCapacitor) return { handled: false, started: false };
    var plugin = nativeScreenRecorder();
    if (!plugin || typeof plugin.start !== 'function') return { handled: false, started: false };
    var res = await plugin.start();
    return { handled: true, started: res?.started === true };
  }

  async function stopScreenRecording() {
    if (!isCapacitor) return null;
    var plugin = nativeScreenRecorder();
    if (!plugin || typeof plugin.stop !== 'function') return null;
    return await plugin.stop();
  }

  async function cancelScreenRecording() {
    if (!isCapacitor) return;
    var plugin = nativeScreenRecorder();
    if (!plugin || typeof plugin.cancel !== 'function') return;
    try { await plugin.cancel(); } catch (_) {}
  }

  async function pickVideo() {
    if (!isCapacitor) return null;
    var plugin = nativeScreenRecorder();
    if (!plugin || typeof plugin.pickVideo !== 'function') return null;
    return await plugin.pickVideo();
  }

  async function updateProgressNotification(
    title: string,
    body: string,
    progress: number,
    ongoing: boolean = true,
    id: number = 9981,
  ) {
    if (!isCapacitor) return;
    var transfer = nativeFileTransfer();
    if (transfer && typeof transfer.updateProgressNotification === 'function') {
      try {
        await transfer.updateProgressNotification({
          id: id,
          title: String(title || 'ServerNox'),
          body: String(body || ''),
          progress: Math.max(0, Math.min(100, Math.round(progress))),
          ongoing: Boolean(ongoing),
        });
        return;
      } catch (_) {}
    }
  }

  async function cancelProgressNotification(id: number = 9981) {
    if (!isCapacitor) return;
    var transfer = nativeFileTransfer();
    if (transfer && typeof transfer.cancelProgressNotification === 'function') {
      try {
        await transfer.cancelProgressNotification({ id: id });
      } catch (_) {}
    }
    await cancelNotification(id);
  }

  function nativeBiometricAuth() {
    return isCapacitor ? nativeWindow.Capacitor?.Plugins?.NativeBiometricAuth : undefined;
  }

  async function biometricAuthAvailable() {
    if (!isCapacitor) return { available: false, hasEnrolled: false };
    var plugin = nativeBiometricAuth();
    if (!plugin || typeof plugin.isAvailable !== 'function') return { available: false, hasEnrolled: false };
    try {
      var res = await plugin.isAvailable();
      return { available: res?.available === true, hasEnrolled: res?.hasEnrolled === true };
    } catch (_) {
      return { available: false, hasEnrolled: false };
    }
  }

  async function performBiometricAuth(options?: { title?: string; subtitle?: string; description?: string; negativeButtonText?: string }) {
    if (!isCapacitor) return { success: true, bypassed: true };
    var plugin = nativeBiometricAuth();
    if (!plugin || typeof plugin.authenticate !== 'function') return { success: true, bypassed: true };
    try {
      var res = await plugin.authenticate({
        title: options?.title || 'Kimlik Doğrulama',
        subtitle: options?.subtitle || 'İşlemi onaylamak için parmak izinizi okutun',
        description: options?.description || 'ServerNox Güvenlik Onayı',
        negativeButtonText: options?.negativeButtonText || 'İptal',
      });
      return {
        success: res?.success === true,
        cancelled: res?.cancelled === true,
        message: res?.message || '',
      };
    } catch (err: any) {
      return {
        success: false,
        cancelled: false,
        message: err?.message || 'Biyometrik doğrulama başarısız',
      };
    }
  }

  function nativeMediaSession() {
    return isCapacitor ? nativeWindow.Capacitor?.Plugins?.NativeMediaSession : undefined;
  }

  async function updateMediaSession(params: {
    title: string;
    artist?: string;
    album?: string;
    isPlaying: boolean;
    duration?: number;
    position?: number;
  }) {
    if (!isCapacitor) return;
    var plugin = nativeMediaSession();
    if (!plugin || typeof plugin.updateSession !== 'function') return;
    try {
      await plugin.updateSession({
        title: params.title || 'ServerNox',
        artist: params.artist || '',
        album: params.album || '',
        isPlaying: Boolean(params.isPlaying),
        duration: Number(params.duration || 0),
        position: Number(params.position || 0),
      });
    } catch (_) {}
  }

  async function clearMediaSession() {
    if (!isCapacitor) return;
    var plugin = nativeMediaSession();
    if (!plugin || typeof plugin.clearSession !== 'function') return;
    try {
      await plugin.clearSession();
    } catch (_) {}
  }

  function addMediaActionListener(listener: (data: { action: string; position?: number }) => void) {
    if (!isCapacitor) return () => {};
    var plugin = nativeMediaSession();
    if (!plugin || typeof plugin.addListener !== 'function') return () => {};
    var handlePromise = plugin.addListener('mediaAction', (data: any) => {
      try {
        if (data && typeof data.action === 'string') {
          listener(data);
        }
      } catch (_) {}
    });
    return () => {
      try {
        if (handlePromise && typeof handlePromise.then === 'function') {
          handlePromise.then((h: any) => { if (h && typeof h.remove === 'function') h.remove(); }).catch(() => {});
        }
      } catch (_) {}
    };
  }

  nativeWindow.SCPNative = {
    updateMediaSession: updateMediaSession,
    clearMediaSession: clearMediaSession,
    addMediaActionListener: addMediaActionListener,
    isCapacitor: isCapacitor,
    notify: notify,
    cancelNotification: cancelNotification,
    updateProgressNotification: updateProgressNotification,
    cancelProgressNotification: cancelProgressNotification,
    requestNotificationPermission: requestNotificationPermission,
    notificationPermissionStatus: notificationPermissionStatus,
    registerFcmPush: registerFcmPush,
    unregisterCurrentFcmEndpoint: unregisterCurrentFcmEndpoint,
    setPreference: setPreference,
    getPreference: getPreference,
    uploadFromDevice: uploadFromDevice,
    downloadToDevice: downloadToDevice,
    downloadUrlToDevice: downloadUrlToDevice,
    subscribeDownloads: subscribeDownloads,
    listNativeDownloads: listNativeDownloads,
    cancelNativeDownload: cancelNativeDownload,
    retryNativeDownload: retryNativeDownload,
    openNativeDownload: openNativeDownload,
    dismissNativeDownload: dismissNativeDownload,
    downloadPublicFile: downloadPublicFile,
    saveTextToDevice: saveTextToDevice,
    openExternalUrl: openExternalUrl,
    speechRecognitionAvailable: speechRecognitionAvailable,
    startSpeechRecognition: startSpeechRecognition,
    setTerminalKeyboardRaw: setTerminalKeyboardRaw,
    screenRecordingAvailable: screenRecordingAvailable,
    startScreenRecording: startScreenRecording,
    stopScreenRecording: stopScreenRecording,
    cancelScreenRecording: cancelScreenRecording,
    pickVideo: pickVideo,
    biometricAuthAvailable: biometricAuthAvailable,
    performBiometricAuth: performBiometricAuth,
    syncNativeShortcuts: syncNativeShortcuts,
  };

  // Android launcher shortcuts
  window.addEventListener('servernox:shortcut', function (event: any) {
    var path = event?.detail?.path;
    if (path) {
      navigateApp(path);
    }
  });

  if (isCapacitor) {
    window.addEventListener('servernox:favorites-updated', function () {
      syncNativeShortcuts().catch(function () {});
    });

    if ((nativeWindow as any).__servernox_initial_route) {
      var initRoute = (nativeWindow as any).__servernox_initial_route;
      delete (nativeWindow as any).__servernox_initial_route;
      navigateApp(initRoute);
    } else {
      getNativeInitialRoute().then(function (route) {
        if (route) {
          navigateApp(route);
        }
      }).catch(function () {});
    }

    setTimeout(function () {
      syncNativeShortcuts().catch(function () {});
    }, 500);

    window.setTimeout(() => {
      cancelNotification(9981);
    }, 1000);
  }
})();
